import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import '../styles/Recruitment.css';

const OPTION_LABELS = ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H'];

const Recruitment = ({ lang }) => {
  const [ref, inView] = useInView({ threshold: 0.1, triggerOnce: true });
  const [recruitmentData, setRecruitmentData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [answers, setAnswers] = useState({});
  const [applicantInfo, setApplicantInfo] = useState({ fullName: '', email: '' });
  const [submitStatus, setSubmitStatus] = useState(null); // null | 'sending' | 'success' | 'error'

  useEffect(() => {
    const fetchRecruitment = async () => {
      try {
        const res = await fetch('/api/recruitment');
        const data = await res.json();
        setRecruitmentData(data);
      } catch (err) {
        console.error('Failed to fetch recruitment data:', err);
        setRecruitmentData({ isOpen: false });
      } finally {
        setLoading(false);
      }
    };
    fetchRecruitment();
  }, []);

  const handleInputChange = (questionId, value) => {
    setAnswers((prev) => ({ ...prev, [questionId]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitStatus('sending');
    try {
      const res = await fetch('/api/recruitment/submit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          fullName: applicantInfo.fullName.trim(),
          email: applicantInfo.email.trim(),
          answers
        }),
      });
      const data = await res.json();
      if (data.success) {
        setSubmitStatus('success');
      } else {
        setSubmitStatus('error');
      }
    } catch (err) {
      setSubmitStatus('error');
    }
  };

  const isTextarea = (questionText) => {
    const lower = questionText.toLowerCase();
    return lower.includes('mô tả') || lower.includes('giới thiệu');
  };

  const renderQuestion = (question, index) => {
    const { id, question: questionText, type, options, required } = question;

    return (
      <motion.div
        className="recruitment-question"
        key={id}
        initial={{ opacity: 0, y: 20 }}
        animate={inView ? { opacity: 1, y: 0 } : {}}
        transition={{ delay: 0.2 + index * 0.08, duration: 0.4 }}
      >
        <label className="recruitment-question-label">
          <span className="question-number">{String(index + 1).padStart(2, '0')}</span>
          {questionText}
          {required && <span className="required-asterisk">*</span>}
        </label>

        {type === 'text' && (
          isTextarea(questionText) ? (
            <textarea
              className="recruitment-textarea"
              value={answers[id] || ''}
              onChange={(e) => handleInputChange(id, e.target.value)}
              required={required}
              rows="4"
              placeholder={lang.RECRUITMENT_PLACEHOLDER_TEXTAREA || 'Nhập câu trả lời của bạn...'}
            />
          ) : (
            <input
              type="text"
              className="recruitment-input"
              value={answers[id] || ''}
              onChange={(e) => handleInputChange(id, e.target.value)}
              required={required}
              placeholder={lang.RECRUITMENT_PLACEHOLDER_INPUT || 'Nhập câu trả lời...'}
            />
          )
        )}

        {type === 'multiple_choice' && (
          <div className="recruitment-options">
            {options.map((option, optIndex) => (
              <label
                key={optIndex}
                className={`recruitment-radio-option ${answers[id] === option ? 'selected' : ''}`}
              >
                <input
                  type="radio"
                  name={id}
                  value={option}
                  checked={answers[id] === option}
                  onChange={(e) => handleInputChange(id, e.target.value)}
                  required={required}
                />
                <span className="radio-custom">
                  <span className="radio-dot"></span>
                </span>
                <span className="option-label">{OPTION_LABELS[optIndex] || String.fromCharCode(65 + optIndex)}</span>
                <span className="option-text">{option}</span>
              </label>
            ))}
          </div>
        )}
      </motion.div>
    );
  };

  const renderContactSidebar = () => (
    <div className="recruitment-sidebar">
      <div className="sidebar-card">
        <h3 className="sidebar-title">{lang.CONTACT_LABEL || 'Contact'}</h3>

        <div className="sidebar-item">
          <div className="sidebar-icon">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
              <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" strokeLinecap="round" strokeLinejoin="round"/>
              <polyline points="22,6 12,13 2,6" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </div>
          <div>
            <span className="sidebar-item-label">Email</span>
            <span className="sidebar-item-value">{lang.CONTACT_EMAIL || 'ispclub@ptit.edu.vn'}</span>
          </div>
        </div>

        <div className="sidebar-item">
          <div className="sidebar-icon">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
              <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" strokeLinecap="round" strokeLinejoin="round"/>
              <circle cx="12" cy="10" r="3" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </div>
          <div>
            <span className="sidebar-item-label">{lang.CONTACT_ADDRESS_LABEL || 'Address'}</span>
            <span className="sidebar-item-value">{lang.CONTACT_ADDRESS || 'PTIT, Ha Noi, Viet Nam'}</span>
          </div>
        </div>

        <div className="sidebar-socials">
          <a href={lang.CONTACT_FACEBOOK || '#'} className="sidebar-social-link" target="_blank" rel="noreferrer" aria-label="Facebook">
            <svg viewBox="0 0 24 24" fill="currentColor">
              <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
            </svg>
          </a>
          <a href={lang.CONTACT_DISCORD || '#'} className="sidebar-social-link" target="_blank" rel="noreferrer" aria-label="Discord">
            <svg viewBox="0 0 24 24" fill="currentColor">
              <path d="M20.317 4.3698a19.7913 19.7913 0 00-4.8851-1.5152.0741.0741 0 00-.0785.0371c-.211.3753-.4447.8648-.6083 1.2495-1.8447-.2762-3.68-.2762-5.4868 0-.1636-.3933-.4058-.8742-.6177-1.2495a.077.077 0 00-.0785-.037 19.7363 19.7363 0 00-4.8852 1.515.0699.0699 0 00-.0321.0277C.5334 9.0458-.319 13.5799.0992 18.0578a.0824.0824 0 00.0312.0561c2.0528 1.5076 4.0413 2.4228 5.9929 3.0294a.0777.0777 0 00.0842-.0276c.4616-.6304.8731-1.2952 1.226-1.9942a.076.076 0 00-.0416-.1057c-.6528-.2476-1.2743-.5495-1.8722-.8923a.077.077 0 01-.0076-.1277c.1258-.0943.2517-.1923.3718-.2914a.0743.0743 0 01.0776-.0105c3.9278 1.7933 8.18 1.7933 12.0614 0a.0739.0739 0 01.0785.0095c.1202.099.246.1981.3728.2924a.077.077 0 01-.0066.1276 12.2986 12.2986 0 01-1.873.8914.0766.0766 0 00-.0407.1067c.3604.698.7719 1.3628 1.225 1.9932a.076.076 0 00.0842.0286c1.961-.6067 3.9495-1.5219 6.0023-3.0294a.077.077 0 00.0313-.0552c.5004-5.177-.8382-9.6739-3.5485-13.6604a.061.061 0 00-.0312-.0286zM8.02 15.3312c-1.1825 0-2.1569-1.0857-2.1569-2.419 0-1.3332.9555-2.4189 2.157-2.4189 1.2108 0 2.1757 1.0952 2.1568 2.419 0 1.3332-.9555 2.4189-2.1569 2.4189zm7.9748 0c-1.1825 0-2.1569-1.0857-2.1569-2.419 0-1.3332.9554-2.4189 2.1569-2.4189 1.2108 0 2.1757 1.0952 2.1568 2.419 0 1.3332-.946 2.4189-2.1568 2.4189z"/>
            </svg>
          </a>
        </div>
      </div>
    </div>
  );

  // Loading state
  if (loading) {
    return (
      <section className="recruitment section" id="recruitment">
        <div className="container">
          <div className="recruitment-loading">
            <div className="loading-spinner"></div>
            <p>{lang.RECRUITMENT_LOADING || 'Loading...'}</p>
          </div>
        </div>
      </section>
    );
  }

  // Closed state
  if (!recruitmentData?.isOpen) {
    return (
      <section className="recruitment section" id="recruitment" ref={ref}>
        <div className="container">
          <motion.div
            className="recruitment-closed"
            initial={{ opacity: 0, y: 40 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.7 }}
          >
            <div className="closed-decoration closed-decoration-top"></div>
            <div className="closed-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <rect x="3" y="11" width="18" height="11" rx="2" ry="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M7 11V7a5 5 0 0 1 10 0v4" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <h2 className="closed-title">
              {lang.RECRUITMENT_CLOSED_TITLE || 'Recruitment is Currently Closed'}
            </h2>
            <p className="closed-desc">
              {lang.RECRUITMENT_CLOSED_DESC || 'We are not accepting applications at this time. Please check back later or follow our socials for updates.'}
            </p>
            <div className="closed-divider"></div>
            <div className="closed-contact">
              <div className="closed-contact-item">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                  <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" strokeLinecap="round" strokeLinejoin="round"/>
                  <polyline points="22,6 12,13 2,6" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                <span>{lang.CONTACT_EMAIL || 'ispclub@ptit.edu.vn'}</span>
              </div>
              <div className="closed-contact-item">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                  <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" strokeLinecap="round" strokeLinejoin="round"/>
                  <circle cx="12" cy="10" r="3" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                <span>{lang.CONTACT_ADDRESS || 'PTIT, Ha Noi, Viet Nam'}</span>
              </div>
              <div className="closed-socials">
                <a href={lang.CONTACT_FACEBOOK || '#'} className="closed-social-link" target="_blank" rel="noreferrer" aria-label="Facebook">
                  <svg viewBox="0 0 24 24" fill="currentColor">
                    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                  </svg>
                </a>
                <a href={lang.CONTACT_DISCORD || '#'} className="closed-social-link" target="_blank" rel="noreferrer" aria-label="Discord">
                  <svg viewBox="0 0 24 24" fill="currentColor">
                    <path d="M20.317 4.3698a19.7913 19.7913 0 00-4.8851-1.5152.0741.0741 0 00-.0785.0371c-.211.3753-.4447.8648-.6083 1.2495-1.8447-.2762-3.68-.2762-5.4868 0-.1636-.3933-.4058-.8742-.6177-1.2495a.077.077 0 00-.0785-.037 19.7363 19.7363 0 00-4.8852 1.515.0699.0699 0 00-.0321.0277C.5334 9.0458-.319 13.5799.0992 18.0578a.0824.0824 0 00.0312.0561c2.0528 1.5076 4.0413 2.4228 5.9929 3.0294a.0777.0777 0 00.0842-.0276c.4616-.6304.8731-1.2952 1.226-1.9942a.076.076 0 00-.0416-.1057c-.6528-.2476-1.2743-.5495-1.8722-.8923a.077.077 0 01-.0076-.1277c.1258-.0943.2517-.1923.3718-.2914a.0743.0743 0 01.0776-.0105c3.9278 1.7933 8.18 1.7933 12.0614 0a.0739.0739 0 01.0785.0095c.1202.099.246.1981.3728.2924a.077.077 0 01-.0066.1276 12.2986 12.2986 0 01-1.873.8914.0766.0766 0 00-.0407.1067c.3604.698.7719 1.3628 1.225 1.9932a.076.076 0 00.0842.0286c1.961-.6067 3.9495-1.5219 6.0023-3.0294a.077.077 0 00.0313-.0552c.5004-5.177-.8382-9.6739-3.5485-13.6604a.061.061 0 00-.0312-.0286zM8.02 15.3312c-1.1825 0-2.1569-1.0857-2.1569-2.419 0-1.3332.9555-2.4189 2.157-2.4189 1.2108 0 2.1757 1.0952 2.1568 2.419 0 1.3332-.9555 2.4189-2.1569 2.4189zm7.9748 0c-1.1825 0-2.1569-1.0857-2.1569-2.419 0-1.3332.9554-2.4189 2.1569-2.4189 1.2108 0 2.1757 1.0952 2.1568 2.419 0 1.3332-.946 2.4189-2.1568 2.4189z"/>
                  </svg>
                </a>
              </div>
            </div>
            <div className="closed-decoration closed-decoration-bottom"></div>
          </motion.div>
        </div>
      </section>
    );
  }

  // Success state after submission
  if (submitStatus === 'success') {
    return (
      <section className="recruitment section" id="recruitment" ref={ref}>
        <div className="container">
          <motion.div
            className="recruitment-success"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5 }}
          >
            <div className="success-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" strokeLinecap="round" strokeLinejoin="round"/>
                <polyline points="22 4 12 14.01 9 11.01" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <h2 className="success-title">
              {lang.RECRUITMENT_SUCCESS_TITLE || 'Application Submitted!'}
            </h2>
            <p className="success-desc">
              {lang.RECRUITMENT_SUCCESS_DESC || 'Thank you for applying. We will review your application and get back to you soon.'}
            </p>
          </motion.div>
        </div>
      </section>
    );
  }

  // Open state - show form
  const questions = recruitmentData?.form?.questions || [];

  return (
    <section className="recruitment section" id="recruitment" ref={ref}>
      <div className="container">
        <motion.div
          className="recruitment-header"
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
        >
          <div className="terminal-section-header">
                <div className="terminal-section-titlebar">
                  root@ispclub:~/# cd /ispclub/recruitment
                </div>
                <div className="terminal-section-body">
                  <span className="terminal-sh-line">&gt;_ sh recruitment.sh</span>
                </div>
              </div>
          <h2 className="section-title">{lang.RECRUITMENT_TITLE || 'Join ISP Club'}</h2>
          <p className="section-subtitle">
            {lang.RECRUITMENT_SUBTITLE || 'Passionate about information security? Apply now!'}
          </p>
        </motion.div>

        <div className="recruitment-layout">
          <motion.div
            className="recruitment-form-wrapper"
            initial={{ opacity: 0, y: 40 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.15, duration: 0.6 }}
          >
            <form className="recruitment-form" onSubmit={handleSubmit}>
              <div className="form-status-bar">
                <span className="status-dot"></span>
                <span>{recruitmentData?.campaign?.gen
                  ? `Now Recruiting Gen ${recruitmentData.campaign.gen} Members`
                  : (recruitmentData?.campaign?.name || lang.RECRUITMENT_STATUS || 'Now Accepting Applications')}</span>
              </div>

              {/* Built-in fields: Full Name & Email */}
              <motion.div
                className="recruitment-question"
                initial={{ opacity: 0, y: 20 }}
                animate={inView ? { opacity: 1, y: 0 } : {}}
                transition={{ delay: 0.15, duration: 0.4 }}
              >
                <label className="recruitment-question-label">
                  <span className="question-number">01</span>
                  {lang.RECRUITMENT_FULLNAME || 'Full Name'}
                  <span className="required-asterisk">*</span>
                </label>
                <input
                  type="text"
                  className="recruitment-input"
                  value={applicantInfo.fullName}
                  onChange={(e) => setApplicantInfo({ ...applicantInfo, fullName: e.target.value })}
                  required
                  placeholder={lang.RECRUITMENT_FULLNAME_PLACEHOLDER || 'Enter your full name...'}
                />
              </motion.div>
              <motion.div
                className="recruitment-question"
                initial={{ opacity: 0, y: 20 }}
                animate={inView ? { opacity: 1, y: 0 } : {}}
                transition={{ delay: 0.2, duration: 0.4 }}
              >
                <label className="recruitment-question-label">
                  <span className="question-number">02</span>
                  {lang.RECRUITMENT_EMAIL || 'Email Address'}
                  <span className="required-asterisk">*</span>
                </label>
                <input
                  type="email"
                  className="recruitment-input"
                  value={applicantInfo.email}
                  onChange={(e) => setApplicantInfo({ ...applicantInfo, email: e.target.value })}
                  required
                  placeholder={lang.RECRUITMENT_EMAIL_PLACEHOLDER || 'example@email.com'}
                />
              </motion.div>

              {questions.map((q, i) => renderQuestion(q, i + 2))}

              <AnimatePresence>
                {submitStatus === 'error' && (
                  <motion.p
                    className="recruitment-error"
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0 }}
                  >
                    {lang.RECRUITMENT_ERROR || 'An error occurred. Please try again.'}
                  </motion.p>
                )}
              </AnimatePresence>

              <button
                type="submit"
                className="btn btn-primary recruitment-submit"
                disabled={submitStatus === 'sending'}
              >
                {submitStatus === 'sending'
                  ? (lang.RECRUITMENT_SUBMITTING || 'Submitting...')
                  : (lang.RECRUITMENT_SUBMIT || 'Submit Application')}
                {submitStatus !== 'sending' && (
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <path d="M3 8h10M9 4l4 4-4 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                )}
              </button>
            </form>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{ delay: 0.3, duration: 0.6 }}
          >
            {renderContactSidebar()}
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Recruitment;
